import { AddVolunteerToMultipleEventsRequest, CreateEventRequest, EventResponse } from '@codey2/codey-api-types/models';
import { usePrivateApi } from './usePrivateApi';
import { useCallback, useState } from 'react';
import { codeyApiClient } from './codeyApiClient';

export function useEventsApi() {
  const [isLoading, setIsLoading] = useState(false);
  const { doPost, doDelete } = usePrivateApi();

  const getAllEvents = useCallback(async (): Promise<EventResponse[]> => {
    try {
      setIsLoading(true);
      return await codeyApiClient.get<EventResponse[]>('/events/public');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getEventById = useCallback(async (eventId: number): Promise<EventResponse> => {
    try {
      setIsLoading(true);
      return await codeyApiClient.get<EventResponse>(`/events/public/${eventId}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createEvent = useCallback(
    async (request: CreateEventRequest): Promise<EventResponse> => {
      try {
        setIsLoading(true);
        return await doPost<CreateEventRequest, EventResponse>('/events', request);
      } finally {
        setIsLoading(false);
      }
    },
    [doPost]
  );

  const deleteEvent = useCallback(
    async (eventId: number): Promise<void> => {
      try {
        setIsLoading(true);
        await doDelete(`/events/${eventId}`);
      } finally {
        setIsLoading(false);
      }
    },
    [doDelete]
  );

  const addVolunteersToEvent = useCallback(
    async (eventId: number, volunteerIds: number[]): Promise<EventResponse> => {
      try {
        setIsLoading(true);
        return await doPost<undefined, EventResponse>(`/events/${eventId}/volunteers?ids=${volunteerIds.join(',')}`, undefined);
      } finally {
        setIsLoading(false);
      }
    },
    [doPost]
  );

  const removeVolunteersFromEvent = useCallback(
    async (eventId: number, volunteerIds: number[]): Promise<void> => {
      try {
        setIsLoading(true);
        await doDelete(`/events/${eventId}/volunteers?ids=${volunteerIds.join(',')}`);
      } finally {
        setIsLoading(false);
      }
    },
    [doDelete]
  );

  const addVolunteerToMultipleEvents = useCallback(
    async (request: AddVolunteerToMultipleEventsRequest): Promise<void> => {
      try {
        setIsLoading(true);
        await doPost<AddVolunteerToMultipleEventsRequest, void>('/events/volunteers/batch', request);
      } finally {
        setIsLoading(false);
      }
    },
    [doPost]
  );

  return {
    getAllEvents,
    getEventById,
    createEvent,
    deleteEvent,
    addVolunteersToEvent,
    removeVolunteersFromEvent,
    addVolunteerToMultipleEvents,
    isLoading,
  };
}
